import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Season, type CompanyLocation } from "@mysnowscape/snow-lib";
import {
  CompanyServiceOption,
  CompanyServicesBySeason,
  RouteBuilderProperty,
  RouteGenerationInputs,
} from "../../../../../types/routeBuilder";
import routeBuilder from "../../../../../services/axios/routeBuilder/index";
import { defaultInputs } from "../lib/defaultInputs";
import { applyDefaultServiceTime } from "../lib/generationProperties";
import {
  recencyToServicedAfter,
  shouldShowRecency,
} from "../lib/serviceHelpers";

export function useRouteBuilderResources(inputs: RouteGenerationInputs) {
  const companyId = useSelector(
    (state: { currentUser?: { currentCompanyId?: string } }) =>
      state.currentUser?.currentCompanyId ?? "",
  );

  const queryClient = useQueryClient();
  const locationsKey = useMemo(
    () => ["route-builder", "locations", companyId] as const,
    [companyId],
  );
  const locationsQuery = useQuery({
    queryKey: locationsKey,
    queryFn: () => routeBuilder.loadBuilderLocations({ companyId }),
    enabled: Boolean(companyId),
  });
  const servicesQuery = useQuery({
    queryKey: ["route-builder", "services", companyId],
    queryFn: () => routeBuilder.loadBuilderServices({ companyId }),
    enabled: Boolean(companyId),
  });

  const serviceTypesKey = (inputs.propertyFilters.serviceTypes ?? [])
    .slice()
    .sort()
    .join(",");
  const recencyOfService = inputs.propertyFilters.recencyOfService ?? null;
  const showRecency = shouldShowRecency(inputs.routeType, inputs.season);

  const servicedAfter = useMemo(
    () => (showRecency ? recencyToServicedAfter(recencyOfService) : undefined),
    [showRecency, recencyOfService],
  );
  const propertiesQuery = useQuery({
    queryKey: [
      "route-builder",
      "properties",
      companyId,
      inputs.season,
      serviceTypesKey,
      servicedAfter ?? null,
    ],
    queryFn: () =>
      routeBuilder.loadBuilderProperties({
        companyId,
        season: inputs.season,
        services: inputs.propertyFilters.serviceTypes,
        servicedAfter,
        includeAssignedRoutes: true,
      }),
    enabled: Boolean(companyId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === companyId ? previousData : undefined,
  });

  const locations = locationsQuery.data ?? [];
  const companyServices = useMemo<CompanyServicesBySeason>(
    () => servicesQuery.data ?? { snow: [], landscape: [] },
    [servicesQuery.data],
  );
  // Keep estimates in state; the flag controls whether requests and totals use them.
  const allProperties = useMemo<RouteBuilderProperty[]>(
    () =>
      applyDefaultServiceTime(
        propertiesQuery.data ?? [],
        inputs.defaultServiceTimeMinutes,
      ),
    [propertiesQuery.data, inputs.defaultServiceTimeMinutes],
  );
  const setLocations = useCallback(
    (updater: (previous: CompanyLocation[]) => CompanyLocation[]) => {
      queryClient.setQueryData<CompanyLocation[]>(locationsKey, (previous) =>
        updater(previous ?? []),
      );
    },
    [locationsKey, queryClient],
  );

  const availableServices = useMemo((): CompanyServiceOption[] => {
    return inputs.season === Season.landscape
      ? companyServices.landscape
      : companyServices.snow;
  }, [companyServices, inputs.season]);

  return {
    companyId,
    locations,
    setLocations,
    allProperties,
    availableServices,
    isLoadingResources:
      locationsQuery.isLoading ||
      servicesQuery.isLoading ||
      propertiesQuery.isLoading,
    isRefreshingProperties: propertiesQuery.isFetching,
  };
}

export { defaultInputs };
